import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Trash2, Filter, ChevronLeft, ChevronRight, Package, Eye, EyeOff, Barcode, QrCode } from 'lucide-react';
import { productService } from '../services/productService';
import { categoryService } from '../services/categoryService';
import { supplierService } from '../services/supplierService';
import { ProductResponseDTO, ProductFilters } from '../types/product';
import { CategoryResponseDTO } from '../types/category';
import { FornecedorResponseDTO } from '../types/supplier';
import { alertService } from '../services/alertService';

const ProductsPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<ProductResponseDTO[]>([]);
  const [categories, setCategories] = useState<CategoryResponseDTO[]>([]);
  const [suppliers, setSuppliers] = useState<FornecedorResponseDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filters, setFilters] = useState<ProductFilters>({
    nome: '',
    codigo: '',
    categoriaId: '',
    fornecedorId: '',
    ativo: undefined
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(itemsPerPage);
  const [pageNumber, setPageNumber] = useState(1);



  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData, suppliersData] = await Promise.all([
        productService.list({ page: currentPage - 1, size: itemsPerPage }),
        categoryService.listActive(),
        supplierService.getActive()
      ]);
      
      setProducts(productsData.data.content);
      setTotalPages(productsData.data.totalPages);
      setTotalElements(productsData.data.totalElements ?? productsData.data.content.length);
      setPageSize(productsData.data.size ?? itemsPerPage);
      setPageNumber((productsData.data.number ?? 0) + 1);
      setCategories(categoriesData.data.content);
      setSuppliers(suppliersData.data.content);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alertService.toast('error', 'Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Debounce de 300ms para o termo de busca
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Resetar página para 1 quando o termo debounced mudar
  useEffect(() => {
    if (!loading) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = debouncedSearchTerm
        ? await productService.searchByTerm(debouncedSearchTerm, { page: currentPage - 1, size: itemsPerPage })
        : (filters.categoriaId
            ? await productService.getByCategory(filters.categoriaId, { page: currentPage - 1, size: itemsPerPage })
            : await productService.list({ 
                page: currentPage - 1, 
                size: itemsPerPage,
                ...filters
              }));
      
      setProducts(response.data.content);
      setTotalPages(response.data.totalPages);
      setTotalElements(response.data.totalElements ?? response.data.content.length);
      setPageSize(response.data.size ?? itemsPerPage);
      setPageNumber((response.data.number ?? 0) + 1);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      alertService.toast('error', 'Erro ao carregar produtos.');
    } finally {
      setLoading(false);
    }
  };

  // Recarregar produtos quando filtros ou página mudarem
  useEffect(() => {
    if (!loading) {
      loadProducts();
    }
  }, [currentPage, filters, debouncedSearchTerm]);

  // Paginação baseada na API
  const indexOfLastProduct = pageNumber * pageSize;
  const indexOfFirstProduct = indexOfLastProduct - pageSize;
  const displayTotalPages = totalPages;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (newFilters: Partial<ProductFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      nome: '',
      codigo: '',
      categoriaId: '',
      fornecedorId: '',
      ativo: undefined
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleView = (product: ProductResponseDTO) => {
    navigate(`/produtos/${product.id}/visualizar`);
  };

  

  const handleDelete = async (productId: string) => {
    const result = await alertService.confirmDelete(
      'Excluir produto',
      'Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita!',
      'Sim, excluir',
      'Cancelar'
    );
    
    if (result.isConfirmed) {
      try {
        await productService.delete(productId);
        loadProducts();
        alertService.toast('success', 'O produto foi excluído com sucesso!');
      } catch (error) {
        console.error('Erro ao excluir produto:', error);
        alertService.toast('error', 'Não foi possível excluir o produto. Tente novamente.');
      }
    }
  };

  const handleToggleStatus = async (product: ProductResponseDTO) => {
    try {
      if (product.ativo) {
        await productService.deactivate(product.id);
        alertService.toast('success', 'Produto desativado com sucesso!');
      } else {
        await productService.activate(product.id);
        alertService.toast('success', 'Produto ativado com sucesso!');
      }
      loadProducts();
    } catch (error) {
      console.error('Erro ao alterar status do produto:', error);
      alertService.toast('error', 'Erro ao alterar status do produto.');
    }
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.nome || 'Sem categoria';
  };

  const getSupplierName = (supplierId: string | undefined) => {
    if (!supplierId) return 'Sem fornecedor';
    const supplier = suppliers.find(sup => sup.id === supplierId);
    return supplier?.razaoSocial || 'Sem fornecedor';
  };

  const getCategoryColor = (categoryId: string) => {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-purple-100 text-purple-800',
      'bg-green-100 text-green-800',
      'bg-yellow-100 text-yellow-800',
      'bg-indigo-100 text-indigo-800',
      'bg-pink-100 text-pink-800',
      'bg-red-100 text-red-800'
    ];
    const index = categoryId ? categoryId.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  const getStatusColor = (ativo: boolean) => {
    return ativo 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const getStockColor = (stock: number) => {
    if (stock === 0) return 'text-red-600';
    if (stock <= 5) return 'text-yellow-600';
    return 'text-green-600';
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Produtos</h1>
          <p className="text-gray-600">Gerencie o catálogo de produtos</p>
        </div>
        <button
          onClick={() => {
            navigate('/produtos/novo');
          }}
          className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="h-5 w-5 mr-2" />
          Novo Produto
        </button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total de Produtos</p>
              <p className="text-xl font-bold text-gray-900">{products.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-green-600 font-bold">✓</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Produtos Ativos</p>
              <p className="text-xl font-bold text-gray-900">
                {products.filter(p => p.ativo).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-yellow-100 rounded-lg flex items-center justify-center">
              <span className="text-yellow-600 font-bold">!</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Estoque Baixo</p>
              <p className="text-xl font-bold text-gray-900">
                {products.filter(p => p.ativo).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-red-100 rounded-lg flex items-center justify-center">
              <span className="text-red-600 font-bold">×</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Sem Estoque</p>
              <p className="text-xl font-bold text-gray-900">
                {products.filter(p => !p.ativo).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Busca */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, código ou código de barras..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filtro por Categoria */}
          <div className="lg:w-48">
            <select
              value={filters.categoriaId}
              onChange={(e) => handleFilterChange({ categoriaId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">Todas as categorias</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.nome}</option>
              ))}
            </select>
          </div>

          {/* Filtro por Fornecedor */}
          <div className="lg:w-48">
            <select
              value={filters.fornecedorId}
              onChange={(e) => handleFilterChange({ fornecedorId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">Todos os fornecedores</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>{supplier.razaoSocial}</option>
              ))}
            </select>
          </div>

          {/* Filtro por Status */}
          <div className="lg:w-40">
            <select
              value={filters.ativo === undefined ? '' : filters.ativo.toString()}
              onChange={(e) => handleFilterChange({ ativo: e.target.value === '' ? undefined : e.target.value === 'true' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            >
              <option value="">Todos os status</option>
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>

          {/* Botão Limpar Filtros */}
          <div className="lg:w-auto">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Limpar
            </button>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Código
                      </th>
                       <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Produto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Categoria
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fornecedor
                      </th>
                      
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 flex items-start text-sm font-medium">Código: {product.codigo}</div>
                    {product.codigoBarras && (
                      <div className="text-xs text-gray-500 flex items-start text-sm font-medium">Barras: {product.codigoBarras}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 cursor-pointer" onClick={() => handleView(product)}>{product.nome}</div>
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(product.categoriaId)}`}>
                      {getCategoryName(product.categoriaId)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getSupplierName(product.fornecedorId)}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(product.ativo)}`}>
                      {product.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleView(product)}
                        className="text-gray-600 hover:text-gray-900 p-1 rounded"
                        title="Detalhe"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(product)}
                        className={`p-1 rounded ${
                          product.ativo 
                            ? 'text-red-600 hover:text-red-900' 
                            : 'text-green-600 hover:text-green-900'
                        }`}
                        title={product.ativo ? 'Desativar' : 'Ativar'}
                      >
                        {product.ativo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="text-red-600 hover:text-red-900 p-1 rounded"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {displayTotalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === displayTotalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Mostrando{' '}
                  <span className="font-medium">{Math.min(indexOfFirstProduct + 1, totalElements || products.length)}</span>
                  {' '}até{' '}
                  <span className="font-medium">{Math.min(indexOfLastProduct, totalElements || products.length)}</span>
                  {' '}de{' '}
                  <span className="font-medium">{totalElements || products.length}</span>
                  {' '}resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  {Array.from({ length: displayTotalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === displayTotalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default ProductsPage;
